import { useEffect, useRef, useState } from 'react';
import {
  PREVIEW_MESSAGE_SOURCE,
  isPreviewMessage,
  type PreviewRenderMessage,
  type PreviewRpcResponse,
} from '../../../investorPreview/previewProtocol';
import type { PreviewHandlers } from './contentPreviewHandlers';

const DEFAULT_HEIGHT = 320;

const reply = (target: Window, message: Omit<PreviewRpcResponse, 'source' | 'kind'>) =>
  target.postMessage(
    { source: PREVIEW_MESSAGE_SOURCE, kind: 'rpc-result', ...message },
    window.location.origin,
  );

/** Answers one call from the iframe with its allow-listed handler, or refuses it. */
export function answerPreviewCall(
  frameWindow: Window,
  handlers: PreviewHandlers,
  id: number,
  method: string,
  args: unknown[],
): void {
  const handler = Object.prototype.hasOwnProperty.call(handlers, method)
    ? handlers[method]
    : undefined;
  if (!handler) {
    reply(frameWindow, { id, error: `"${method}" is not available in preview` });
    return;
  }
  handler(...args).then(
    (result) => reply(frameWindow, { id, result }),
    (error: unknown) =>
      reply(frameWindow, { id, error: error instanceof Error ? error.message : String(error) }),
  );
}

/**
 * The admin side of the investor-preview protocol for one iframe: accepts messages only from that
 * iframe's window on this origin, answers its calls from `handlers` (anything else is refused),
 * tells it what to render (again whenever it reloads or the props change), and tracks the height
 * it reports so the iframe can be sized to its content.
 */
export function useInvestorPreviewBridge(
  frameRef: React.RefObject<HTMLIFrameElement | null>,
  view: string,
  viewProps: Record<string, unknown>,
  handlers: PreviewHandlers,
): { height: number; rendered: boolean } {
  const [height, setHeight] = useState(DEFAULT_HEIGHT);
  const [ready, setReady] = useState(false);
  const [rendered, setRendered] = useState(false);
  const latest = useRef({ view, viewProps, handlers });
  latest.current = { view, viewProps, handlers };

  const sendRender = (frameWindow: Window) => {
    const message: PreviewRenderMessage = {
      source: PREVIEW_MESSAGE_SOURCE,
      kind: 'render',
      view: latest.current.view,
      props: latest.current.viewProps,
    };
    frameWindow.postMessage(message, window.location.origin);
  };

  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      const frameWindow = frameRef.current?.contentWindow;
      if (!frameWindow || event.source !== frameWindow) return;
      if (event.origin !== window.location.origin) return;
      const data = event.data;
      if (!isPreviewMessage(data)) return;

      if (data.kind === 'ready') {
        // Also sent again whenever the iframe reloads, so always answer with the latest view
        // (and show the spinner until it has rendered again).
        setReady(true);
        setRendered(false);
        sendRender(frameWindow);
      } else if (data.kind === 'rendered') {
        setRendered(true);
      } else if (data.kind === 'height') {
        setHeight(Math.max(0, Math.ceil(data.height)));
      } else if (data.kind === 'rpc') {
        answerPreviewCall(frameWindow, latest.current.handlers, data.id, data.method, data.args);
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
    // Everything else is read through `latest`, so the listener never goes stale.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [frameRef]);

  const renderKey = JSON.stringify({ view, viewProps });
  useEffect(() => {
    const frameWindow = frameRef.current?.contentWindow;
    if (ready && frameWindow) sendRender(frameWindow);
    // renderKey captures view + viewProps by value, so equal props don't re-render the iframe.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, renderKey]);

  return { height, rendered };
}
